import { useRef, type ReactNode } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import { staggerContainer, fadeUp } from '@/lib/animations';

const HEIGHTS = {
  compact: 'h-[300px] md:h-[380px]',
  standard: 'h-[400px] md:h-[500px]',
  tall: 'h-[480px] md:h-[600px]',
} as const;

interface Props {
  photo: string;
  eyebrow: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Position de cadrage photo (par défaut 'center 30%'). */
  objectPosition?: string;
  /** Hauteur du bandeau (par défaut 'standard'). */
  height?: keyof typeof HEIGHTS;
}

/**
 * Bandeau cinematic réutilisable pour les pages publiques.
 * Photo plein écran + parallax + cercles cuivre + texte stagger fade-up.
 *
 * Désactive le parallax + scale au scroll sur mobile et si
 * prefers-reduced-motion. L'image reste fixe en object-cover.
 */
export default function PublicPageHero({
  photo,
  eyebrow,
  title,
  description,
  objectPosition = 'center 30%',
  height = 'standard',
}: Props) {
  const ref = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const disableScrollFx = prefersReducedMotion || isMobile;
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    disableScrollFx ? ['0%', '0%'] : ['0%', '20%']
  );
  const scale = useTransform(
    scrollYProgress,
    [0, 1],
    disableScrollFx ? [1, 1] : [1, 1.1]
  );

  return (
    <section ref={ref} className={`relative ${HEIGHTS[height]} overflow-hidden`}>
      <motion.img
        src={photo}
        alt=""
        loading="eager"
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        style={{ y, scale, objectPosition }}
        className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
      />
      <div className="absolute inset-0 bg-[#0A0A0A]/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/50 via-transparent to-[#0A0A0A]" />

      <div className="pointer-events-none absolute -top-24 -right-16 h-[300px] w-[300px] rounded-full border border-[#D4A574]/15" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-[250px] w-[250px] rounded-full border border-[#D4A574]/15" />

      <motion.div
        className="relative z-10 h-full max-w-5xl mx-auto px-5 lg:px-8 flex flex-col items-center justify-center text-center"
        initial="hidden"
        animate="visible"
        variants={staggerContainer(0.12, 0.3)}
      >
        <motion.div
          variants={fadeUp}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4A574] mb-4"
        >
          <span className="h-px w-8 bg-[#D4A574]/40" />
          {eyebrow}
          <span className="h-px w-8 bg-[#D4A574]/40" />
        </motion.div>
        <motion.h1
          variants={fadeUp}
          className="font-display text-4xl md:text-6xl text-[#E8E0D6] leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
        >
          {title}
        </motion.h1>
        {description && (
          <motion.p
            variants={fadeUp}
            className="mt-5 text-neutral-200 max-w-2xl leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          >
            {description}
          </motion.p>
        )}
      </motion.div>
    </section>
  );
}
